import { useEffect, useMemo, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { DrawingModule } from "./drawing-module";
import { DrawingAnnotationLayer } from "./drawing-annotation-layer";
import { DrawingPlacementOverlay } from "./drawing-placement-overlay";
import {
  DrawingViewerCanvasDock,
  DrawingViewerMarkupBar,
} from "./drawing-viewer-panel";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// react-pdf renders the PDF page onto a <canvas> at `scale` (× devicePixelRatio
// internally). We used to hardcode scale=1 and do all zooming with a CSS
// `transform: scale(zoom)` on the wrapper — cheap for panning, but it means the
// canvas itself never had more pixels than the page's native 72dpi size, so
// zooming into a large/detailed drawing just stretched a low-res bitmap (and
// the region "snapshot" capture, which reads pixels straight off this canvas,
// inherited the same blur). Instead we render the canvas at a higher internal
// resolution and counter-scale it back down with CSS so its on-screen size
// (and all the pin/overlay math built on `pdfBaseSize`) is unchanged.
const BASE_RENDER_MULTIPLIER = 2.5; // headroom over native size even at zoom 1
const MAX_CANVAS_DIMENSION = 5000; // guard against huge canvases on big sheets
const ZOOM_SETTLE_MS = 220; // re-rasterize shortly after zoom stops changing, not every frame

const useDrawingRenderScale = (zoom, pdfBaseSize) => {
  const [settledZoom, setSettledZoom] = useState(zoom);

  useEffect(() => {
    const id = setTimeout(() => setSettledZoom(zoom), ZOOM_SETTLE_MS);
    return () => clearTimeout(id);
  }, [zoom]);

  return useMemo(() => {
    const devicePixelRatio =
      typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    const desired = Math.max(BASE_RENDER_MULTIPLIER, settledZoom);
    const longestSide = Math.max(pdfBaseSize?.width || 0, pdfBaseSize?.height || 0);
    const maxByBudget =
      longestSide > 0
        ? MAX_CANVAS_DIMENSION / (longestSide * devicePixelRatio)
        : desired;
    return Math.max(1, Math.min(desired, maxByBudget));
  }, [settledZoom, pdfBaseSize?.width, pdfBaseSize?.height]);
};

export const DrawingViewerCanvas = ({
  t,
  containerRef,
  sheetRef,
  pdfUrl,
  page,
  numPages,
  zoom,
  pan,
  pdfBaseSize,
  pdfError,
  drawing,
  modules,
  pendingChanges,
  selectedModule,
  draftRect,
  draftPen,
  draftMeasure,
  strokeWidth,
  tool,
  markupColor,
  isShapeTool,
  workspaceClass,
  measureScale,
  isVersionMode,
  activeVersion,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onSelectModule,
  onModuleMove,
  onModuleConfigure,
  onModuleDelete,
  onOpenModuleDetail,
  onFocusOnDrawing,
  onPdfLoadSuccess,
  onPdfLoadError,
  onPageLoadSuccess,
  onZoomChange,
  onFit,
  onPageChange,
  onMarkupColorChange,
  pendingObservation,
  placementGhost,
  onCancelPlacement,
  onWantSketchChange,
  relocatingModuleId = null,
  isModuleDraggable,
  onStartRelocate,
  onRetrySync,
  checklists = [],
  ifcContextMenu,
  onContextMenu,
  onOpenPlaceIn3d,
  onCloseContextMenu,
  ifcAlignmentActive,
  ifcAlignmentPoints = [],
  onCancelIfcAlignment,
}) => {
  const renderScale = useDrawingRenderScale(zoom, pdfBaseSize);

  return (
    <div className="relative min-h-0 min-w-0 max-w-full flex-1 overflow-x-hidden overscroll-none">
      <div
        ref={containerRef}
        className={`relative h-full touch-none overflow-hidden overscroll-none bg-[radial-gradient(120%_120%_at_50%_0%,#282b33_0%,#1d2026_100%)] ${workspaceClass}`}
        style={{ touchAction: "none", WebkitUserSelect: "none", userSelect: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onContextMenu={onContextMenu}
      >
        {isVersionMode && activeVersion && (
          <div className="pointer-events-none absolute left-1/2 top-3 z-[24] -translate-x-1/2 rounded-full border border-[#2b6ef0]/30 bg-[#2b6ef0]/90 px-3 py-1 text-xs font-semibold text-white shadow-lg">
            {t("drawings.viewer.editing_version")}: {activeVersion.title}
          </div>
        )}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
            maskImage:
              "radial-gradient(120% 90% at 50% 40%, #000 40%, transparent 100%)",
          }}
        />

        <div
          className="absolute left-0 top-0 will-change-transform"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: "0 0",
          }}
        >
          <div
            ref={sheetRef}
            className="relative rounded-[2px] bg-white shadow-[0_18px_50px_rgba(0,0,0,0.45),0_4px_14px_rgba(0,0,0,0.30)]"
            style={{
              width: pdfBaseSize.width > 0 ? pdfBaseSize.width : "auto",
              height: pdfBaseSize.height > 0 ? pdfBaseSize.height : "auto",
              lineHeight: 0,
              overflow: "hidden",
            }}
          >
            {pdfUrl && (
              <Document
                file={pdfUrl}
                onLoadSuccess={onPdfLoadSuccess}
                onLoadError={onPdfLoadError}
                loading={
                  <div className="flex h-40 w-80 items-center justify-center bg-white px-4 py-3 text-sm text-[#59616d]">
                    {t("drawings.viewer.loading_pdf")}
                  </div>
                }
              >
                <div style={{ transform: `scale(${1 / renderScale})`, transformOrigin: "0 0" }}>
                  <Page
                    pageNumber={page + 1}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    scale={renderScale}
                    onLoadSuccess={onPageLoadSuccess}
                  />
                </div>
              </Document>
            )}

            {pdfError && (
              <div className="rounded-[10px] border border-red-200 bg-red-50 p-4 text-red-700">
                <p className="font-semibold">{t("drawings.viewer.pdf_error")}</p>
                <p className="mt-1 text-sm">{pdfError}</p>
                {drawing?.document?.id && (
                  <p className="mt-2 text-sm">
                    {t("drawings.document")}: {drawing.document.id}
                  </p>
                )}
              </div>
            )}

            <DrawingAnnotationLayer
              modules={modules}
              draftPen={draftPen}
              draftMeasure={draftMeasure}
              strokeWidth={strokeWidth}
              measureScale={measureScale}
            />

            <div className="absolute inset-0 z-10">
                {modules.map((module) => (
                  <DrawingModule
                    key={module.id}
                    module={module}
                    pendingChanges={pendingChanges}
                    sheetSize={pdfBaseSize}
                    isSelected={selectedModule?.id === module.id}
                    checklists={checklists}
                    zoom={zoom}
                    draggable={
                      tool === "select" &&
                      !pendingObservation &&
                      !relocatingModuleId &&
                      isModuleDraggable?.(module) !== false
                    }
                    onSelect={onSelectModule}
                    onMove={onModuleMove}
                    onConfigure={onModuleConfigure}
                    onDelete={onModuleDelete}
                    onOpenDetail={onOpenModuleDetail}
                    onFocusOnDrawing={onFocusOnDrawing}
                    onStartRelocate={onStartRelocate}
                    onRetrySync={onRetrySync}
                    t={t}
                  />
                ))}
            </div>

            {ifcAlignmentPoints.map((point, index) => (
              <div
                key={`ifc-align-${index}`}
                className="pointer-events-none absolute grid h-5 w-5 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-2 border-white bg-[#2b6ef0] text-[10px] font-bold text-white"
                style={{ left: point.x, top: point.y }}
              >
                {index + 1}
              </div>
            ))}

            {draftRect && (
              <div
                className="pointer-events-none absolute border-2 border-dashed border-[#2b6ef0] bg-[#2b6ef0]/10"
                style={{
                  left: Math.min(draftRect.x, draftRect.x + draftRect.w),
                  top: Math.min(draftRect.y, draftRect.y + draftRect.h),
                  width: Math.abs(draftRect.w),
                  height: Math.abs(draftRect.h),
                }}
              />
            )}
          </div>
        </div>

        <DrawingPlacementOverlay
          t={t}
          pending={pendingObservation}
          ghost={placementGhost}
          onCancel={onCancelPlacement}
          onWantSketchChange={onWantSketchChange}
        />

        {ifcAlignmentActive && (
          <div
            className="absolute left-1/2 top-3 z-[26] flex -translate-x-1/2 items-center gap-3 rounded-full border border-[#2b6ef0]/30 bg-[#2b6ef0]/90 px-3 py-1 text-xs font-semibold text-white shadow-lg"
            /* clicks in the banner must not be picked up as alignment points */
            onPointerDown={(event) => event.stopPropagation()}
          >
            {t("drawings.viewer.align_3d_pick_hint")}
            <button
              type="button"
              onClick={onCancelIfcAlignment}
              className="rounded-full bg-white/20 px-2 py-0.5 text-[11px] hover:bg-white/30"
            >
              {t("common.cancel")}
            </button>
          </div>
        )}

        {ifcContextMenu && (
          <>
            <div className="absolute inset-0 z-[28]" onClick={onCloseContextMenu} />
            <div
              className="absolute z-[29] min-w-[190px] overflow-hidden rounded-[10px] border border-[#e5e8ec] bg-white py-1 shadow-lg"
              style={{ left: ifcContextMenu.left, top: ifcContextMenu.top }}
              /* the workspace closes the menu on pointer down — keep clicks here alive */
              onPointerDown={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                onClick={() =>
                  onOpenPlaceIn3d(ifcContextMenu.pdfX, ifcContextMenu.pdfY)
                }
                className="block w-full px-3 py-2 text-left text-[13px] font-medium text-[#14171c] hover:bg-[#f6f7f9]"
              >
                {t("drawings.viewer.open_place_in_3d")}
              </button>
            </div>
          </>
        )}

        <DrawingViewerMarkupBar
          t={t}
          color={markupColor}
          onColorChange={onMarkupColorChange}
          visible={isShapeTool}
        />
      </div>

      {/* Outside overflow-hidden so fixed mobile dock is not clipped */}
      <DrawingViewerCanvasDock
        t={t}
        zoom={zoom}
        page={page}
        pageCount={numPages}
        onZoomChange={onZoomChange}
        onFit={onFit}
        onPageChange={onPageChange}
      />
    </div>
  );
};
